import * as React from 'react';
import { ModalContainer, LabelButton, Icon } from '@teamsnap/snap-ui';
import { AuthContext, Role, isOneOf } from 'auth';

type Props = {
  isOpen: boolean;
  closeFn: () => void;
  orgId: number;
  onVerifyOrConnect: () => void;
  paymentAccountStatus?: string;
};

const SeasonMerchantAccountUnapprovedModal: React.FC<Props> = ({
  isOpen,
  closeFn,
  orgId,
  onVerifyOrConnect,
  paymentAccountStatus,
}) => {
  const context = React.useContext(AuthContext);
  // Check for a null user to handle Cypress tests
  const isProgramAdmin = context?.user !== null ? isOneOf(`${orgId}`, context.user, Role.ProgramAdmin) : false;

  const headerText =
    paymentAccountStatus === 'under_review'
      ? 'Merchant account under review'
      : paymentAccountStatus === 'rejected'
      ? 'Merchant account rejected'
      : 'Unapproved merchant account';

  const bodyText =
    paymentAccountStatus === 'under_review'
      ? 'The merchant account selected for this season\'s invoices is under review.'
      : paymentAccountStatus === 'rejected'
      ? 'The merchant account selected for this season\'s invoices has been rejected after review.'
      : 'The merchant account selected for this form requires verification.';

  const actionText =
    paymentAccountStatus === 'under_review'
      ? 'Saving with this account selection will prevent invoice payments from being processed until the account is reviewed and approved.'
      : paymentAccountStatus === 'rejected'
      ? 'Saving with this account selected will prevent invoice payments from being processed until a new merchant account is setup and approved.'
      : 'Saving this account selection will prevent registrant payments from being processed.';

  const showVerifyNowButton = paymentAccountStatus !== 'under_review' && paymentAccountStatus !== 'rejected';

  return (
    <ModalContainer
      data-testid="SeasonMerchantAccountUnapprovedModal--container"
      isOpen={isOpen}
      onClose={closeFn}
      closeButton={false}
    >
      <div className="sui-p-2">
        <div className="sui-flex sui-items-center sui-mb-4">
          <Icon filled name="report" size="xl" className="u-colorNegative" />
          <h2 data-testid="SeasonMerchantAccountUnapprovedModal--header" className="sui-heading-sm sui-ml-1">
            {headerText}
          </h2>
        </div>

        <div className="sui-body sui-mb-4">
          <p data-testid="SeasonMerchantAccountUnapprovedModal--body" className="sui-mb-4">
            {bodyText}
          </p>

          {isProgramAdmin ? (
            <p>Account setup must be completed by an organization administrator before you can receive deposits.</p>
          ) : (
            <>
              <p data-testid="SeasonMerchantAccountUnapprovedModal--action" className="sui-mb-4">
                {actionText}
              </p>

              {showVerifyNowButton && (
                <p>
                  Click <strong>Verify now</strong> to proceed with account verification.
                </p>
              )}
            </>
          )}
        </div>

        <div className="sui-flex sui-justify-end">
          <LabelButton
            data-testid="SeasonMerchantAccountUnapprovedModal--close-button"
            onClick={closeFn}
            variantType={isProgramAdmin ? 'primary' : 'secondary'}
          >
            Close
          </LabelButton>

          {!isProgramAdmin && showVerifyNowButton && (
            <LabelButton
              data-testid="SeasonMerchantAccountUnapprovedModal--verify-now-button"
              onClick={onVerifyOrConnect}
              className="sui-ml-1"
            >
              Verify now
            </LabelButton>
          )}
        </div>
      </div>
    </ModalContainer>
  );
};

export default SeasonMerchantAccountUnapprovedModal;
